import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const nordicLight: TemplateRegistration = {
  meta: {
    id: 'nordic-light',
    name: 'Nordic Light',
    category: 'Minimal',
    description: 'Scandinavian-inspired design with bright, airy layouts and functional beauty',
    previewImage: 'https://images.unsplash.com/photo-1494438639946-1ebd1d20bf85?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(0, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Quicksand', 'Nunito']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="reviews-badge">
            <div class="reviews-stars">${renderStars(Math.round(rating))}</div>
            <span class="reviews-text">${rating.toFixed(1)} out of 5 &middot; ${reviewCount} reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-card">
            <span class="promo-badge">${promoDiscount}</span>
            <h3 class="promo-title">${promoTitle}</h3>
            <p class="promo-desc">${promoDesc}</p>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      const pullQuote = truncateAtSentence(safeAbout, 100);
      sections.push(`
      <section id="about" class="section about-section">
        <div class="container">
          <span class="section-label">About Us</span>
          <h2>Our Story</h2>
          <div class="about-layout">
            <div class="about-pullquote">${pullQuote}</div>
            <div class="about-content">
              <p>${safeAbout}</p>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        const icon = s.icon ? getIconSvg(s.icon) : getIconSvg('star');
        return `
          <div class="service-card">
            <div class="service-icon">${icon}</div>
            <div class="service-content">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section services-section">
        <div class="container">
          <span class="section-label">What We Do</span>
          <h2>Our Services</h2>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${escapeAttr(safeName)}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="container">
          <span class="section-label">Our Work</span>
          <h2>Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const testimonialCards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-accent"></div>
            <blockquote>&ldquo;${tContent}&rdquo;</blockquote>
            <div class="testimonial-author">
              <div class="author-avatar">${tName.charAt(0)}</div>
              <div>
                <strong>${tName}</strong>
                ${tRole ? `<span class="author-role">${tRole}</span>` : ''}
              </div>
            </div>
            ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section">
        <div class="container">
          <span class="section-label">Testimonials</span>
          <h2>What People Say</h2>
          <div class="testimonials-grid">${testimonialCards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3>${q}</h3>
            <p>${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="container-narrow">
          <span class="section-label">FAQ</span>
          <h2>Common Questions</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section">
        <div class="container-narrow">
          <span class="section-label">Pricing</span>
          <h2>Our Rates</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="container-narrow">
          <span class="section-label">Schedule</span>
          <h2>Business Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section contact-section">
        <div class="container">
          <span class="section-label">Get in Touch</span>
          <h2>Contact Us</h2>
          <div class="contact-cards">
            <div class="contact-card">
              <h3>Phone</h3>
              ${enabledFeatures.has('phone-display') && phone ? `<a href="tel:${escapeAttr(phone)}" class="contact-chip">${getIconSvg('phone')}<span>${sanitizeText(phone)}</span></a>` : `<p style="color:var(--text-light);font-size:.9rem;">Call us anytime</p>`}
            </div>
            <div class="contact-card">
              <h3>Address</h3>
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-chip">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : `<p style="color:var(--text-light);font-size:.9rem;">Visit our location</p>`}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">Open in Maps &rarr;</a>` : ''}
            </div>
            <div class="contact-card">
              <h3>Message Us</h3>
              <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
                <input type="text" placeholder="Your name" required>
                <input type="email" placeholder="Your email" required>
                <textarea placeholder="How can we help?" rows="3" required></textarea>
                <button type="submit">Send</button>
              </form>
            </div>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #f8fafc; --bg-alt: #f1f5f9; --text: #334155; --text-light: #64748b;
      --accent: #0ea5e9; --accent-light: #e0f2fe; --secondary: #94a3b8;
      --white: #ffffff; --border: #e2e8f0; --radius: 12px;
      --font-heading: 'Quicksand', sans-serif; --font-body: 'Nunito', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.7; font-size: 16px; }
    .container { max-width: 1100px; margin: 0 auto; padding: 0 32px; }
    .container-narrow { max-width: 720px; margin: 0 auto; padding: 0 32px; }
    a { color: var(--accent); text-decoration: none; transition: color .2s; }
    a:hover { color: #0284c7; }

    /* NAV */
    .navbar { padding: 20px 0; background: var(--white); border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 100; }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; max-width: 1100px; margin: 0 auto; padding: 0 32px; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.3rem; font-weight: 700; color: var(--text); text-decoration: none; }
    .nav-brand:hover { color: var(--accent); }
    .nav-links { display: flex; gap: 28px; list-style: none; }
    .nav-links a { color: var(--text-light); font-size: .9rem; font-weight: 600; text-decoration: none; transition: color .2s; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; padding: 4px; }
    .nav-toggle span { display: block; width: 24px; height: 2px; background: var(--text); margin: 5px 0; border-radius: 2px; transition: .3s; }

    /* HERO */
    .hero { padding: 100px 0 80px; background: var(--white); overflow: hidden; }
    .hero-inner { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; max-width: 1100px; margin: 0 auto; padding: 0 32px; }
    .hero-content { position: relative; }
    .hero-dot { width: 48px; height: 48px; background: var(--accent-light); border-radius: 50%; position: absolute; top: -20px; left: -16px; z-index: 0; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2rem, 4.5vw, 3rem); font-weight: 700; line-height: 1.25; margin-bottom: 20px; color: var(--text); position: relative; z-index: 1; }
    .hero-text { color: var(--text-light); font-size: 1.05rem; margin-bottom: 32px; line-height: 1.8; max-width: 460px; }
    .hero-cta { display: inline-block; padding: 14px 36px; background: var(--accent); color: var(--white); font-family: var(--font-heading); font-weight: 700; font-size: .95rem; border-radius: var(--radius); transition: background .3s, transform .2s; text-decoration: none; }
    .hero-cta:hover { background: #0284c7; color: var(--white); transform: translateY(-2px); }
    .hero-image { position: relative; }
    .hero-image img { width: 100%; height: 400px; object-fit: cover; border-radius: 20px; box-shadow: 0 8px 40px rgba(14,165,233,.15); }
    .hero-image-placeholder { width: 100%; height: 400px; background: linear-gradient(135deg, var(--accent-light), var(--bg-alt)); border-radius: 20px; display: flex; align-items: center; justify-content: center; color: var(--secondary); font-size: 1rem; }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section:nth-child(even) { background: var(--bg-alt); }
    .section-label { display: inline-block; font-family: var(--font-heading); font-size: .8rem; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--accent); margin-bottom: 8px; }
    .section h2 { font-family: var(--font-heading); font-size: 1.8rem; font-weight: 700; margin-bottom: 48px; color: var(--text); }

    /* ABOUT - pull quote left, text right */
    .about-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: start; }
    .about-pullquote { font-family: var(--font-heading); font-size: 1.4rem; font-weight: 700; color: var(--accent); line-height: 1.5; border-left: 4px solid var(--accent); padding-left: 20px; }
    .about-content p { color: var(--text-light); line-height: 1.9; font-size: 1rem; }

    /* SERVICES - alternating rows (odd: icon left, even: icon right) */
    .services-section { background: var(--bg-alt); }
    .services-grid { display: flex; flex-direction: column; gap: 24px; }
    .service-card { background: var(--white); border-radius: var(--radius); padding: 32px 28px; box-shadow: 0 2px 12px rgba(0,0,0,.04); transition: transform .2s, box-shadow .2s; display: flex; gap: 24px; align-items: center; }
    .service-card:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(0,0,0,.08); }
    .service-card:nth-child(even) { flex-direction: row-reverse; }
    .service-icon { width: 56px; height: 56px; background: var(--accent-light); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: var(--accent); flex-shrink: 0; }
    .service-icon .icon-char { font-size: 24px; line-height: 1; }
    .service-content { flex: 1; }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 10px; color: var(--text); }
    .service-card p { color: var(--text-light); font-size: .92rem; line-height: 1.7; margin-bottom: 12px; }
    .service-price { font-family: var(--font-heading); font-weight: 700; color: var(--accent); font-size: .95rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .gallery-item img { width: 100%; height: 240px; object-fit: cover; border-radius: var(--radius); transition: transform .3s; }
    .gallery-item img:hover { transform: scale(1.03); }

    /* TESTIMONIALS */
    .testimonials-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
    .testimonial-card { background: var(--white); border-radius: var(--radius); padding: 28px; position: relative; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,.04); }
    .testimonial-accent { position: absolute; top: 0; left: 0; width: 4px; height: 100%; background: var(--accent); border-radius: 0 4px 4px 0; }
    .testimonial-card blockquote { font-size: .95rem; color: var(--text); line-height: 1.8; margin-bottom: 20px; padding-left: 16px; }
    .testimonial-author { display: flex; align-items: center; gap: 12px; }
    .author-avatar { width: 40px; height: 40px; border-radius: 50%; background: var(--accent-light); color: var(--accent); display: flex; align-items: center; justify-content: center; font-family: var(--font-heading); font-weight: 700; font-size: .95rem; }
    .testimonial-author strong { font-size: .9rem; display: block; }
    .author-role { font-size: .8rem; color: var(--text-light); }
    .testimonial-stars { margin-top: 12px; padding-left: 16px; }
    .star { color: var(--border); font-size: .9rem; }
    .star.filled { color: #f59e0b; }

    /* GOOGLE REVIEWS */
    .google-reviews { padding: 32px 0; background: var(--accent-light); }
    .reviews-badge { display: flex; align-items: center; justify-content: center; gap: 16px; flex-wrap: wrap; }
    .reviews-text { color: var(--text-light); font-size: .9rem; font-weight: 600; }
    .reviews-stars .star { font-size: 1.1rem; }

    /* PROMO */
    .promo-banner { padding: 40px 0; }
    .promo-card { background: linear-gradient(135deg, var(--accent-light), #f0f9ff); border-radius: var(--radius); padding: 36px; text-align: center; border: 1px solid #bae6fd; }
    .promo-badge { display: inline-block; background: var(--accent); color: var(--white); padding: 6px 16px; border-radius: 20px; font-size: .8rem; font-weight: 700; margin-bottom: 12px; }
    .promo-title { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 700; margin-bottom: 8px; }
    .promo-desc { color: var(--text-light); font-size: .92rem; }

    /* FAQ */
    .faq-list { display: flex; flex-direction: column; gap: 4px; }
    .faq-item { background: var(--white); border-radius: var(--radius); padding: 24px 28px; }
    .faq-item h3 { font-family: var(--font-heading); font-size: 1rem; font-weight: 700; margin-bottom: 10px; color: var(--text); }
    .faq-item p { color: var(--text-light); font-size: .92rem; line-height: 1.7; }

    /* PRICE LIST */
    .price-list { display: flex; flex-direction: column; gap: 4px; }
    .price-row { display: flex; justify-content: space-between; align-items: center; background: var(--white); border-radius: var(--radius); padding: 20px 28px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1rem; font-weight: 700; margin-bottom: 4px; }
    .price-info p { color: var(--text-light); font-size: .85rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.15rem; font-weight: 700; color: var(--accent); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; }
    .hours-list li { padding: 14px 0; border-bottom: 1px solid var(--border); color: var(--text-light); font-size: .92rem; }
    .hours-list li:last-child { border-bottom: none; }

    /* CONTACT - three-col cards */
    .contact-section { background: var(--white); }
    .contact-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .contact-card { background: var(--bg-alt); border-radius: var(--radius); padding: 28px 24px; }
    .contact-card h3 { font-family: var(--font-heading); font-size: 1rem; font-weight: 700; margin-bottom: 12px; color: var(--text); }
    .contact-chip { display: flex; align-items: center; gap: 10px; font-size: .9rem; color: var(--text); text-decoration: none; margin-bottom: 8px; }
    .contact-chip .icon-char { font-size: 18px; line-height: 1; color: var(--accent); }
    .map-link { font-size: .85rem; font-weight: 600; }
    .contact-form { display: flex; flex-direction: column; gap: 12px; }
    .contact-form input, .contact-form textarea { background: var(--white); border: 2px solid transparent; border-radius: var(--radius); padding: 12px 14px; font-family: var(--font-body); font-size: .92rem; color: var(--text); outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form input::placeholder, .contact-form textarea::placeholder { color: var(--secondary); }
    .contact-form button { padding: 12px 24px; background: var(--accent); border: none; color: var(--white); font-family: var(--font-heading); font-weight: 700; font-size: .9rem; border-radius: var(--radius); cursor: pointer; transition: background .3s, transform .2s; }
    .contact-form button:hover { background: #0284c7; transform: translateY(-2px); }

    /* FOOTER */
    .footer { padding: 40px 0; text-align: center; background: var(--white); border-top: 1px solid var(--border); }
    .footer p { font-size: .85rem; color: var(--text-light); }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; }
      .nav-links.show { display: flex; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: var(--white); padding: 20px 32px; border-bottom: 1px solid var(--border); gap: 16px; }
      .nav-toggle { display: block; }
      .hero { padding: 80px 0 60px; }
      .hero-inner { grid-template-columns: 1fr; gap: 40px; }
      .hero-image img, .hero-image-placeholder { height: 280px; }
      .about-layout { grid-template-columns: 1fr; }
      .section { padding: 60px 0; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-cards { grid-template-columns: 1fr; }
      .service-card:nth-child(even) { flex-direction: row; }
    }
    @media (max-width: 480px) {
      .testimonials-grid { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; align-items: flex-start; gap: 8px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-inner">
      <div class="hero-content">
        <div class="hero-dot"></div>
        <h1>${safeHeadline}</h1>
        <p class="hero-text">${truncateAtSentence(safeAbout)}</p>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get in Touch</a>` : ''}
      </div>
      <div class="hero-image">
        ${galleryImages.length > 0 ? `<img src="${escapeAttr(galleryImages[0])}" alt="${escapeAttr(safeName)}">` : `<div class="hero-image-placeholder">${safeName}</div>`}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
